import { useL10n } from "@ews/react-localization-context";
import { InputChangeEventDetail, InputInputEventDetail } from "@ionic/core";
import { IonButton, IonIcon, IonInput, IonItem, IonLabel, IonNote } from "@ionic/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIsMobile } from "../../../Responsive";
import globalStyle from '../GlobalSettings.module.scss';
import style from './SettingsTextInput.module.scss';
import { Props } from "./types";
import { useAlert } from "../../../Dialog";
export function selectInputValue(ref: React.RefObject<HTMLIonInputElement | HTMLIonSearchbarElement>)
{
    try {
        if ("nativeInput" in (ref.current || {})) {
            (ref.current as unknown as { nativeInput: HTMLInputElement | null; })?.nativeInput?.select();
        }
    } catch { }
}

const ErrorNote: React.FC<{ errorText: string; }> = ({ errorText }) => <IonNote className={style.errorNote} color={"danger"}>{errorText}</IonNote>;


const SettingsTextInput: React.FC<Props> = ({
    endText = '',
    startText = '',
    onChange,
    textAlign,
    inputType = 'text',
    value,
    hidden = false,
    labelPosition = 'stacked',
    text = '',
    disabled,
    hasFocus,
    required = false,
    lines = undefined,
    debounce,
    tooltip,
    onFocus,
    onClick,
    onBlur,
    onClear,
    onEnter,
    id,
    placeholder,
    inputRef,
    icon,
    onIconClick,
    onKeyDown,
    className,
    errorText
}) =>
{
    const { translate: t } = useL10n();
    const mobileView = useIsMobile();
    const inputClass = mobileView ? globalStyle.mobileInput : globalStyle.webInput;
    const InputValue = useRef<string | number | null | undefined>(value);
    const [_, setReload] = useState(false);
    const innerInputRef = useRef<HTMLIonInputElement>(null);
    const alert = useAlert();


    const showMessage = async (message: string) =>
    {
        await alert({
            "title": t(text),
            "message": t(message),
            "buttonTitle": t('COM_TEMPLATES_TEMPLATE_CLOSE'),
            cssClass: style.cancelButton,
            buttonId: "settingsTextInput" + text
        });
    };

    const getElementRef = useCallback(() =>
    {
        return inputRef || innerInputRef;
    }, [inputRef]);

    const _hasFocus = useRef<boolean>(false);

    const getInputValue = useCallback(() =>
    {
        const elementRef = getElementRef();

        return elementRef?.current?.value?.toString();
    }, [getElementRef]);

    const handleFocusAfterReload = useCallback((ref: React.RefObject<HTMLIonInputElement>) =>
    {
        ref.current?.setFocus();
    }, []);

    const handleFocusOut = useCallback(() =>
    {
        _hasFocus.current = false;
    }, []);
    const handleFocusIn = useCallback(() =>
    {
        _hasFocus.current = true;
    }, []);

    const handleRequired = useCallback((value: string | null | undefined) =>
    {
        if (required) {
            return (value !== "" && value !== null && value !== undefined);
        }
        return true;

    }, [required]);


    const handleChange = useCallback((detail: InputInputEventDetail | InputChangeEventDetail) =>
    {
        if (handleRequired(detail.value)) {
            InputValue.current = detail.value;
            if (onChange) onChange(detail.value || '');
        }
    }, [handleRequired, onChange]);

    if (hasFocus) handleFocusAfterReload(getElementRef());

    useEffect(() =>
    {
        InputValue.current = value;
    }, [value]);

    return <><IonItem
        disabled={disabled}
        data-testid="settings-input-container"
        hidden={hidden}
        lines={lines}
        className={`${style.overflow} ${globalStyle.setting} ${hasFocus ? style.hasFocus : ''}`}>
        <div className={style.container}>
            <div className={style.containerInput}>
                {text && <span className={`${style.flex} ${style.justifyStart}`}>
                    <IonLabel
                        data-testid="settings-input-label"
                        className={style.label}
                        style={{ whiteSpace: "break-spaces" }}
                        position={labelPosition}>
                        {`${startText}${t(text)}${endText}`}{required ? <span className={`${globalStyle.required}`}>*</span> : null}
                    </IonLabel>
                    <span className={style.tooltip}>
                        {mobileView ? <></> : tooltip}
                    </span>

                </span>}
                <IonInput
                    data-testid="settings-input"
                    placeholder={placeholder}
                    id={id || undefined}
                    label={``}
                    debounce={debounce}
                    autocomplete={'off'}
                    ref={getElementRef()}
                    onClick={(e) =>
                    {
                        if (onClick) onClick(e);
                    }}
                    onIonFocus={(e) =>
                    {
                        handleFocusIn();
                        if (onFocus) onFocus(e);
                    }
                    }
                    onIonBlur={(e) =>
                    {
                        const input = e.detail?.target as HTMLInputElement;
                        if (!input) return;

                        if (required && input.value === "") {
                            setReload((pre) => !pre);
                            showMessage("[REACT_REQUIRED_VALUE_MESSAGE]");
                        }

                        handleFocusOut();
                        if (onBlur) onBlur({ value: InputValue.current, focus: _hasFocus.current, event: e });
                    }}
                    required={required}
                    disabled={disabled}
                    value={value}
                    type={inputType}
                    className={`${className} ${mobileView && !textAlign ? style.textRight : style.textLeft} `}
                    style={textAlign ? { textAlign } : {}}
                    onKeyDown={(e) =>
                    {
                        const { code } = e;
                        onKeyDown && onKeyDown(e);
                        if (!code || code.toLowerCase() !== "enter") return;
                        const value = getInputValue();

                        if (handleRequired(value)) {
                            if (onEnter) onEnter(value || "");
                        }
                    }}
                    onIonInput={({ detail }) =>
                    {
                        if (debounce) {
                            handleChange(detail);
                        };
                    }}
                    onIonChange={({ detail }) =>
                    {
                        if (!debounce) {
                            handleChange(detail);
                        };

                    }}  //onChange(inputType === 'number' ? (Number(detail.value) || 0) : detail.value || '')}
                >
                    {icon && <IonButton onClick={onIconClick} fill="default" slot={mobileView ? "start" : "end"} aria-label="Find">
                        {icon && (typeof icon === 'string' ? <IonIcon slot="icon-only" icon={icon} aria-hidden="true" /> : icon)}

                    </IonButton>}
                </IonInput>

                {errorText ? <ErrorNote errorText={t(errorText)} /> : null}
            </div>
        </div>
    </IonItem>

    </>;
};

export default SettingsTextInput;
